"use client";
import { gql } from 'graphql-request';
import { useEffect, useRef, useState } from 'react';
import LoadingIndicator from 'src/components/loadingindicator/LoadingIndicator';
import { useFilminfo } from 'src/lib/client/useFilminfo';
import styles from './TrailerPlayer.module.css';
import classNames from "classnames";
import type IFilmklipp from "src/lib/types/Filmklipp";
import type { ITrailer } from "src/lib/types/Trailer";
import getVideoId from "get-video-id";
import YoutubeEmbed from "src/components/youtubembed/YoutubeEmbed";

//#region [Props]
type TrailerPlayerProps = {
    trailer?: ITrailer | IFilmklipp | null;
    streamingTrailer?: string | null; // a youtube id
    className?: string;
};
//#endregion

//#region [Component]
export default function TrailerPlayer({ trailer, streamingTrailer, className }: TrailerPlayerProps) {
    const trailerId = Number(trailer?.videoId ?? 0);
    const trailerEmbedResult = useFilminfo(TRAILER_EMBED_QUERY, { clipId: trailerId, autoplay: true }, { active: !!trailerId });
    // We use our own isLoading in order to control timing of animation.
    // We have a timer to allow animation to finish before the trailer is embedded
    const [isLoading, setIsLoading] = useState(true);

    const timerRef = useRef<number | undefined | null>(undefined);

    const ytId = trailer ? trailer.youtubeId ?? getVideoId(trailer.youtubeUrl ?? "")?.id : streamingTrailer;

    useEffect(() => {
        if (trailerId) {
            if (typeof timerRef.current === "undefined") {
                timerRef.current = window?.setTimeout(() => {
                    timerRef.current = null;
                    // if trailer is loaded BEFORE one second has passed
                    if (trailerEmbedResult?.fiData && !trailerEmbedResult?.fiLoading) {
                        setIsLoading(false);
                    }
                }, 1000);
            }

            // if the trailer loads AFTER one second has passed
            if (timerRef.current === null && trailerEmbedResult?.fiData && !trailerEmbedResult?.fiLoading) {
                setIsLoading(false);
                timerRef.current = null;
            }

            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = undefined;
                }
            }
        }
    }, [trailerEmbedResult, trailerId]);

    useEffect(() => {
        if (ytId) {
            if (typeof timerRef.current === "undefined") {
                timerRef.current = window?.setTimeout(() => {
                    timerRef.current = null;
                    setIsLoading(false);
                }, 1000);
            }

            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = undefined;
                }
            }
        }
    }, [ytId]);



    return <div className={classNames(styles.SPlayerContainer, className)}>
        {isLoading && <LoadingIndicator size='large' />}
        {!isLoading && <>
            {trailerEmbedResult?.fiData && <div dangerouslySetInnerHTML={{ __html: trailerEmbedResult.fiData?.movieQuery?.getMovieClip?.embedCode ?? "" }}></div>}
            {!trailerId && ytId &&
                <YoutubeEmbed youtubeId={ytId} title="Youtube" />
            }
        </>}

    </div>;
}
//#endregion

//#region [Styles]
// Converted to CSS Modules (TrailerPlayer.module.css)
//#endregion

//#region [Other]
const TRAILER_EMBED_QUERY = gql`
	query ($clipId: Int, $autoplay: Boolean) {
		movieQuery {
			getMovieClip(clipId: $clipId, autoplay: $autoplay) {
				__typename
				embedCode
			}
		}
	}
`;
//#endregion
